import { useEffect, useState, useRef } from 'react'
import { Capacitor } from '@capacitor/core'

const SESSION_TIMEOUT_MS = 3 * 24 * 60 * 60 * 1000 // 3日
const CHECK_INTERVAL_MS = 60 * 60 * 1000 // 1時間

export interface UseAutoReloadCheckReturn {
  show_reload_dialog: boolean
  handleReload: () => void
  handleCancel: () => void
}

export const useAutoReloadCheck = (): UseAutoReloadCheckReturn => {
  const [show_reload_dialog, setShowReloadDialog] = useState(false)
  const session_start_time = useRef<number | null>(null)
  const last_check_time = useRef<number>(0)

  useEffect(() => {
    // レンダー中の Date.now() を避け、マウント時に effect 内で初期化する
    if (session_start_time.current === null) session_start_time.current = Date.now()
    // Capacitorの場合は何もしない
    if (Capacitor.isNativePlatform()) return

    const checkReloadNeeded = () => {
      const current_time = Date.now()
      const elapsed_time = current_time - (session_start_time.current ?? current_time)

      // セッションタイムアウト時間経過していない場合は何もしない
      if (elapsed_time <= SESSION_TIMEOUT_MS) return

      // 最後にチェックしてからチェック間隔経過していない場合はスキップ
      const time_since_last_check = current_time - last_check_time.current
      if (time_since_last_check < CHECK_INTERVAL_MS) return

      // チェック時間を記録してダイアログを表示
      last_check_time.current = current_time
      setShowReloadDialog(true)
    }

    // ページが表示されたときにチェック
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkReloadNeeded()
    }

    // Visibility APIでページに戻ったタイミングでチェック
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const handleReload = () => {
    window.location.reload()
  }

  const handleCancel = () => {
    // ダイアログを閉じるだけ（次のチェック間隔後に再度チェック）
    setShowReloadDialog(false)
  }

  return {
    show_reload_dialog,
    handleReload,
    handleCancel
  }
}